import type {
  CalendarReadiness,
  Conversation,
  ConversationId,
  ConversationNote,
  ConversationPage,
  ConversationQuery,
  ConversationThread,
  FollowUp,
  FollowUpChanges,
  FollowUpDraft,
  MediaAsset,
  Message,
  MessagingAvailability,
  MessagingAvailabilityValue,
  MessengerRuntimeConfig,
  MessengerRuntimeFeatures,
  MessengerSendTransport,
  NoteDraft,
  ProviderSendReceipt,
  Recommendation,
  SendIntent,
  SendMessageCommand,
  SuggestionFeedbackRequest,
  SuggestionFeedbackResponse,
} from '@crm/messenger';
import type {
  ConversationReadOptions,
  MessengerRepository,
} from '@crm/messenger';
import {
  MessengerAbortError,
  messengerFailure,
  messengerOk,
  type MessengerFailure,
  type MessengerResult,
} from '@crm/messenger';
import {
  collectionItems,
  firstItem,
  isActiveGifAsset,
  mediaVersionRecord,
  conversationAliasIds,
  normalizeCalendarReadiness,
  normalizeConversation,
  normalizeFollowUp,
  normalizeMediaAsset,
  normalizeMessage,
  normalizeMessagingAvailability,
  normalizeNote,
  normalizeRecommendations,
  normalizeRuntimeFeatures,
  normalizeSuggestionFeedbackResponse,
  normalizeThread,
  asStringArray,
  asRecord,
  textValue,
  type JsonRecord,
} from './mello-messenger-mappers';
import {
  MelloMessengerSendTransport,
  MessengerTransportUnavailableError,
  type McsHubConnector,
} from './mello-messenger-send.transport';

export interface MelloMessengerHttpOptions {
  readonly config: () => MessengerRuntimeConfig;
  readonly tokenProvider: () => Promise<string>;
  readonly fetchImpl?: typeof fetch;
  readonly correlationIdFactory?: () => string;
  readonly readAttempts?: number;
  readonly sendTransport?: MessengerSendTransport;
  readonly mcsConnector?: McsHubConnector;
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined>;
  readonly signal?: AbortSignal;
}

const PROVIDER_READBACK_FAILURE =
  /PROVIDER.*READBACK|READBACK.*PROVIDER|READBACK_UNAVAILABLE/i;

class HttpFailure extends Error {
  constructor(
    readonly failure: MessengerFailure,
    readonly code: string,
  ) {
    super(failure.message);
  }
}

export class MelloMessengerHttpRepository implements MessengerRepository {
  private readonly fetchImpl: typeof fetch;
  private readonly correlationIdFactory: () => string;
  private readonly readAttempts: number;
  private readonly sendTransport: MessengerSendTransport;
  private readonly conversationAliases = new Map<string, Set<string>>();

  constructor(private readonly options: MelloMessengerHttpOptions) {
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
    this.correlationIdFactory =
      options.correlationIdFactory ??
      (() => `mello-messenger-${crypto.randomUUID()}`);
    this.readAttempts = Math.max(1, options.readAttempts ?? 2);
    this.sendTransport =
      options.sendTransport ??
      new MelloMessengerSendTransport({
        request: (path, requestOptions) => this.request(path, requestOptions),
        connector: options.mcsConnector,
      });
  }

  async listConversations(
    query: ConversationQuery,
    signal?: AbortSignal,
  ): Promise<MessengerResult<readonly Conversation[]>> {
    const result = await this.listConversationPage(query, signal);
    return result.kind === 'ok' ? messengerOk(result.value.items) : result;
  }

  listConversationPage(
    query: ConversationQuery,
    signal?: AbortSignal,
  ): Promise<MessengerResult<ConversationPage>> {
    const page = query.page ?? 1;
    const limit = query.limit ?? 100;
    return this.safeRead(async (linked) => {
      const [response, pinResponse] = await Promise.all([
        this.request('/current-user/conversations', {
          signal: linked,
          query: {
            page,
            limit,
            search: query.search,
            includeProviderReadback: false,
          },
        }),
        this.request('/current-user/pinned-conversations', { signal: linked }),
      ]);
      const pinnedIds = new Set(
        asStringArray(asRecord(pinResponse)?.['pinnedConversationIds']),
      );
      const seen = new Map<string, Conversation>();
      for (const item of collectionItems(response)) {
        const conversation = normalizeConversation(item);
        const withPin = {
          ...conversation,
          pinned: conversationAliasIds(item).some((alias) =>
            pinnedIds.has(alias),
          ),
        };
        const identity = (
          conversation.customerPhone || conversation.customerName
        ).toLowerCase();
        const key = `${identity}|${conversation.preview}|${conversation.lastActivityAt}`;
        const existing = seen.get(key);
        if (!existing) {
          seen.set(key, withPin);
        } else if (preferredConversationId(conversation.id, existing.id)) {
          seen.set(key, { ...withPin, pinned: existing.pinned || withPin.pinned });
        } else if (withPin.pinned && !existing.pinned) {
          seen.set(key, { ...existing, pinned: true });
        }
      }
      if (page === 1) this.conversationAliases.clear();
      for (const item of collectionItems(response)) {
        const conversation = normalizeConversation(item);
        const identity = (
          conversation.customerPhone || conversation.customerName
        ).toLowerCase();
        const key = `${identity}|${conversation.preview}|${conversation.lastActivityAt}`;
        const winner = seen.get(key);
        if (!winner) continue;
        const bucket =
          this.conversationAliases.get(winner.id) ?? new Set<string>();
        for (const alias of conversationAliasIds(item)) bucket.add(alias);
        this.conversationAliases.set(winner.id, bucket);
      }
      return {
        items: [...seen.values()],
        hasMore: collectionItems(response).length >= limit,
      };
    }, signal);
  }

  getConversation(
    id: ConversationId,
    signal?: AbortSignal,
    options?: ConversationReadOptions,
  ): Promise<MessengerResult<ConversationThread>> {
    return this.safeRead(async (linked) => {
      const withReadback = options?.includeProviderReadback ?? true;
      const read = (includeProviderReadback: boolean) =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}`,
          {
            signal: linked,
            query: {
              includeMessages: true,
              includeProviderReadback,
              messagePage: options?.messagePage ?? 1,
              messageLimit: options?.messageLimit ?? 100,
              messageWindow: options?.messageWindow ?? 'latest',
            },
          },
        );
      let response: unknown;
      try {
        response = await read(withReadback);
      } catch (error) {
        if (!(error instanceof HttpFailure)) throw error;
        const optionalReadbackFailure =
          PROVIDER_READBACK_FAILURE.test(error.code) ||
          error.failure.kind === 'server' ||
          error.failure.kind === 'offline';
        if (!optionalReadbackFailure) throw error;
        response = await read(!withReadback);
      }
      const item = firstItem(response);
      if (!item) {
        throw new HttpFailure(
          messengerFailure('not-found', 'The conversation was not found.', {
            status: 404,
          }),
          'NOT_FOUND',
        );
      }
      return normalizeThread(item);
    }, signal);
  }

  async sendMessage(
    id: ConversationId,
    command: SendMessageCommand,
    signal?: AbortSignal,
  ): Promise<MessengerResult<Message>> {
    if (command.mediaAssetIds?.length || command.mediaUploads?.length) {
      return this.directSend(id, command, signal);
    }
    try {
      const intent = await this.validateWithReadbackRetry(id, command, signal);
      if (!intent.allowed) {
        return messengerFailure(
          'denied',
          'The messaging service did not allow this message.',
          { status: 403 },
        );
      }
      if (intent.localOnly || !intent.mcsSendPayload) {
        return messengerOk(
          intent.localMessage ?? sentMessageFallback(intent.sendIntentId, command),
        );
      }
      let receipt: ProviderSendReceipt;
      try {
        receipt = await this.sendTransport.send(intent, signal);
      } catch (sendError) {
        if (sendError instanceof MessengerAbortError) throw sendError;
        return this.directSend(id, command, signal);
      }
      return messengerOk(
        await this.sendTransport.commit(id, intent, receipt, signal),
      );
    } catch (error) {
      if (
        error instanceof HttpFailure &&
        PROVIDER_READBACK_FAILURE.test(error.code)
      ) {
        return this.directSend(id, command, signal);
      }
      return this.asFailure(error);
    }
  }

  private async directSend(
    id: ConversationId,
    command: SendMessageCommand,
    signal?: AbortSignal,
  ): Promise<MessengerResult<Message>> {
    try {
      const response = await this.request(
        `/current-user/conversations/${encodeURIComponent(id)}/messages`,
        {
          method: 'POST',
          signal,
          body: {
            content: command.content,
            channelId: command.channelId ??
              (command.mediaAssetIds?.length || command.mediaUploads?.length
                ? 'mms'
                : 'sms'),
            correlationId: this.correlationIdFactory(),
            clientRequestId: command.clientMessageId,
            clientMessageId: command.clientMessageId,
            mediaAssetIds: command.mediaAssetIds?.length
              ? [...command.mediaAssetIds]
              : undefined,
            mediaUploads: command.mediaUploads?.length
              ? [...command.mediaUploads]
              : undefined,
            suggestionBatchId: command.suggestionBatchId,
            suggestionBatchVersion: command.suggestionBatchVersion,
            suggestionId: command.suggestionId,
            triggerMessageId: command.triggerMessageId,
            sendVCard: command.sendVCard === true,
            senderPersona: command.senderPersona,
          },
        },
      );
      const record = asRecord(response);
      const message =
        asRecord(record?.['message']) ?? firstItem(response) ?? record ?? {};
      return messengerOk({
        ...normalizeMessage(message),
        id: textValue(message?.['messageId']) || command.clientMessageId,
        clientMessageId: command.clientMessageId,
        direction: 'outbound',
        status: textValue(message?.['status']) || 'Sent',
      });
    } catch (error) {
      return this.asFailure(error);
    }
  }

  private async validateWithReadbackRetry(
    id: ConversationId,
    command: SendMessageCommand,
    signal?: AbortSignal,
  ): Promise<SendIntent> {
    try {
      return await this.sendTransport.validate(id, command, signal);
    } catch (error) {
      if (
        !(error instanceof HttpFailure) ||
        !PROVIDER_READBACK_FAILURE.test(error.code)
      ) {
        throw error;
      }
      await this.request(
        `/current-user/conversations/${encodeURIComponent(id)}`,
        {
          signal,
          query: { includeMessages: false, includeProviderReadback: true },
        },
      );
      try {
        return await this.sendTransport.validate(id, command, signal);
      } catch (retryError) {
        if (
          retryError instanceof HttpFailure &&
          PROVIDER_READBACK_FAILURE.test(retryError.code)
        ) {
          throw new HttpFailure(
            messengerFailure(
              'server',
              'The messaging provider is temporarily unable to verify this conversation. Try again shortly.',
              { status: retryError.failure.status, retryable: true },
            ),
            retryError.code,
          );
        }
        throw retryError;
      }
    }
  }

  async markRead(id: ConversationId): Promise<MessengerResult<Conversation>> {
    return this.conversationCommand(id, () =>
      this.request(
        `/current-user/conversations/${encodeURIComponent(id)}/read`,
        { method: 'PUT', body: {} },
      ),
    );
  }

  async markUnread(id: ConversationId): Promise<MessengerResult<Conversation>> {
    return this.conversationCommand(id, () =>
      this.request(
        `/current-user/conversations/${encodeURIComponent(id)}/unread`,
        { method: 'PUT', body: {} },
      ),
    );
  }

  async setFlagged(
    id: ConversationId,
    flagged: boolean,
  ): Promise<MessengerResult<Conversation>> {
    return this.conversationCommand(id, () =>
      this.request(
        `/current-user/conversations/${encodeURIComponent(id)}/flag`,
        { method: 'PUT', body: { flagged } },
      ),
    );
  }

  async setPinned(
    id: ConversationId,
    pinned: boolean,
  ): Promise<MessengerResult<Conversation>> {
    try {
      const readback = await this.request('/current-user/pinned-conversations');
      const current = new Set(
        asStringArray(asRecord(readback)?.['pinnedConversationIds']),
      );
      if (pinned) current.add(id);
      else current.delete(id);
      await this.request('/current-user/pinned-conversations', {
        method: 'PUT',
        body: { pinnedConversationIds: [...current] },
      });
      const persisted = await this.request('/current-user/pinned-conversations');
      const persistedIds = new Set(
        asStringArray(asRecord(persisted)?.['pinnedConversationIds']),
      );
      if (persistedIds.has(id) !== pinned) {
        return messengerFailure(
          'conflict',
          'The pinned conversation preference was not persisted.',
          { retryable: true },
        );
      }
      const conversation = await this.readConversation(id);
      return conversation.kind === 'ok'
        ? messengerOk({ ...conversation.value, pinned })
        : conversation;
    } catch (error) {
      return this.asFailure(error);
    }
  }

  async archive(id: ConversationId): Promise<MessengerResult<Conversation>> {
    return this.conversationCommand(id, () =>
      this.request(
        `/current-user/conversations/${encodeURIComponent(id)}/close`,
        { method: 'PUT', body: {} },
      ),
    );
  }

  getRuntimeFeatures(
    signal?: AbortSignal,
  ): Promise<MessengerResult<MessengerRuntimeFeatures>> {
    return this.safeRead(
      (linked) =>
        this.request('/current-user/runtime-features', {
          signal: linked,
        }).then(normalizeRuntimeFeatures),
      signal,
    );
  }

  getRecommendations(
    id: ConversationId,
    signal?: AbortSignal,
  ): Promise<MessengerResult<readonly Recommendation[]>> {
    return this.safeRead(
      (linked) =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}/recommendations`,
          { signal: linked },
        ).then(recommendationsFrom),
      signal,
    );
  }

  regenerateRecommendations(
    id: ConversationId,
  ): Promise<MessengerResult<readonly Recommendation[]>> {
    return this.mutation(
      () =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}` +
            '/recommendations/regenerate',
          { method: 'POST', body: { refreshReason: 'manual-refresh' } },
        ),
      recommendationsFrom,
    );
  }

  async submitSuggestionFeedback(
    id: ConversationId,
    feedback: SuggestionFeedbackRequest,
  ): Promise<MessengerResult<SuggestionFeedbackResponse>> {
    try {
      const response = await this.request(
        `/current-user/conversations/${encodeURIComponent(id)}/recommendations/feedback`,
        {
          method: 'POST',
          body: { ...feedback },
        },
      );
      const normalized = normalizeSuggestionFeedbackResponse(response);
      if (!normalized.accepted) {
        return messengerFailure(
          'validation',
          'Could not save feedback — try again.',
          { retryable: true },
        );
      }
      return messengerOk(normalized);
    } catch (error) {
      return this.asFailure(error);
    }
  }

  listMedia(
    signal?: AbortSignal,
  ): Promise<MessengerResult<readonly MediaAsset[]>> {
    return this.safeRead(async (linked) => {
      const assets = collectionItems(
        await this.request('/media', { signal: linked }),
      ).filter(isActiveGifAsset);
      const resolved = await Promise.all(
        assets.map(async (asset) => {
          const version = mediaVersionRecord(asset);
          const source = asRecord(asset['source']);
          const direct =
            textValue(source?.['safeUrlPreview']) ||
            textValue(asRecord(version?.['storage'])?.['safeUrlPreview']);
          if (direct || !textValue(version?.['mediaVersionId'])) {
            return normalizeMediaAsset(asset, direct);
          }
          try {
            const content = asRecord(
              await this.request(
                `/media/${encodeURIComponent(textValue(asset['assetId']) || textValue(asset['id']))}` +
                  `/versions/${encodeURIComponent(textValue(version?.['mediaVersionId']))}/fetch`,
                { signal: linked },
              ),
            );
            const contentType = textValue(content?.['contentType']);
            const base64 = textValue(content?.['contentBase64']);
            return normalizeMediaAsset(
              asset,
              contentType && base64 ? `data:${contentType};base64,${base64}` : '',
            );
          } catch {
            return normalizeMediaAsset(asset, '');
          }
        }),
      );
      return resolved.filter((asset) => asset.id && asset.url);
    }, signal);
  }

  listFollowUps(
    id: ConversationId,
    signal?: AbortSignal,
  ): Promise<MessengerResult<readonly FollowUp[]>> {
    return this.safeRead(
      (linked) =>
        this.listAcrossAliases(
          id,
          (scope) =>
            `/current-user/conversations/${encodeURIComponent(scope)}/follow-ups`,
          undefined,
          normalizeFollowUp,
          (followUp) => followUp.id,
          (a, b) => a.startsAt.localeCompare(b.startsAt),
          linked,
        ),
      signal,
    );
  }

  createFollowUp(
    id: ConversationId,
    draft: FollowUpDraft,
  ): Promise<MessengerResult<FollowUp>> {
    const startsAt = new Date(draft.startsAt);
    const durationMinutes =
      draft.type === 'call' ? Number(draft.durationMinutes || 30) : undefined;
    return this.mutation(
      () =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}/follow-ups`,
          {
            method: 'POST',
            body: {
              type: draft.type,
              startsAt: startsAt.toISOString(),
              endsAt: durationMinutes
                ? new Date(
                    startsAt.getTime() + durationMinutes * 60000,
                  ).toISOString()
                : undefined,
              timezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
              note: draft.type === 'reminder' ? draft.details : undefined,
              agenda: draft.type === 'call' ? draft.details : undefined,
              channel:
                draft.type === 'call' ? draft.channel || 'phone' : undefined,
              durationMinutes,
              sourceContext: {
                status: 'pending',
                sourceSystem: 'mello-messenger',
              },
              calendar: {
                mutationAttempted: true,
                conflictBehavior: 'suppress-on-conflict',
              },
              idempotencyKey: this.correlationIdFactory(),
              correlationId: this.correlationIdFactory(),
            },
          },
        ),
      (response) => normalizeFollowUp(entityRecord(response)),
    );
  }

  updateFollowUp(
    id: ConversationId,
    followUpId: string,
    changes: FollowUpChanges,
  ): Promise<MessengerResult<FollowUp>> {
    return this.mutation(
      () =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}` +
            `/follow-ups/${encodeURIComponent(followUpId)}`,
          { method: 'PUT', body: { ...changes } },
        ),
      (response) => normalizeFollowUp(entityRecord(response)),
    );
  }

  listNotes(
    id: ConversationId,
    signal?: AbortSignal,
  ): Promise<MessengerResult<readonly ConversationNote[]>> {
    return this.safeRead(
      (linked) =>
        this.listAcrossAliases(
          id,
          (scope) =>
            `/current-user/conversations/${encodeURIComponent(scope)}/notes`,
          { page: 1, limit: 50 },
          normalizeNote,
          (note) => note.id || `${note.body}|${note.createdAt}`,
          (a, b) => a.createdAt.localeCompare(b.createdAt),
          linked,
        ),
      signal,
    );
  }

  createNote(
    id: ConversationId,
    draft: NoteDraft,
  ): Promise<MessengerResult<ConversationNote>> {
    return this.mutation(
      () =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}/notes`,
          { method: 'POST', body: { body: draft.body } },
        ),
      (response) => normalizeNote(entityRecord(response)),
    );
  }

  setNoteArchived(
    id: ConversationId,
    noteId: string,
    archived: boolean,
  ): Promise<MessengerResult<ConversationNote>> {
    return this.mutation(
      () =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}` +
            `/notes/${encodeURIComponent(noteId)}/${archived ? 'archive' : 'unarchive'}`,
          { method: 'PUT', body: {} },
        ),
      (response) => normalizeNote(entityRecord(response)),
    );
  }

  generateNote(
    id: ConversationId,
  ): Promise<MessengerResult<ConversationNote>> {
    return this.mutation(
      () =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}/notes/generate`,
          { method: 'POST', body: {} },
        ),
      (response) => normalizeNote(entityRecord(response)),
    );
  }

  getCalendarReadiness(
    id: ConversationId,
    signal?: AbortSignal,
  ): Promise<MessengerResult<CalendarReadiness>> {
    return this.safeRead(
      (linked) =>
        this.request(
          `/current-user/conversations/${encodeURIComponent(id)}/calendar/readiness`,
          { signal: linked },
        ).then(normalizeCalendarReadiness),
      signal,
    );
  }

  getMessagingAvailability(
    signal?: AbortSignal,
  ): Promise<MessengerResult<MessagingAvailability>> {
    return this.safeRead(
      (linked) =>
        this.request('/current-user/messaging-availability', {
          signal: linked,
        }).then(normalizeMessagingAvailability),
      signal,
    );
  }

  setMessagingAvailability(
    value: MessagingAvailabilityValue,
    expectedVersion: number,
    clientRequestId = `away-ui-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  ): Promise<MessengerResult<MessagingAvailability>> {
    return this.mutation(
      () =>
        this.request('/current-user/messaging-availability/manual', {
          method: 'PUT',
          body: { value, expectedVersion, clientRequestId },
        }),
      normalizeMessagingAvailability,
    );
  }

  private conversationScopes(id: ConversationId): string[] {
    const aliases = this.conversationAliases.get(id);
    return aliases ? [id, ...[...aliases].filter((a) => a !== id)] : [id];
  }

  private async listAcrossAliases<T>(
    id: ConversationId,
    path: (scope: string) => string,
    query: RequestOptions['query'],
    normalize: (item: JsonRecord) => T,
    key: (item: T) => string,
    sort: (a: T, b: T) => number,
    signal?: AbortSignal,
  ): Promise<readonly T[]> {
    const results = await Promise.allSettled(
      this.conversationScopes(id).map((scope) =>
        this.request(path(scope), { signal, query }).then((response) =>
          collectionItems(response).map(normalize),
        ),
      ),
    );
    const merged = new Map<string, T>();
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      for (const item of result.value) merged.set(key(item), item);
    }
    if (
      !merged.size &&
      results.length > 0 &&
      results.every((r) => r.status === 'rejected')
    ) {
      throw (results[0] as PromiseRejectedResult).reason;
    }
    return [...merged.values()].sort(sort);
  }

  private async mutation<T>(
    command: () => Promise<unknown>,
    normalize: (response: unknown) => T,
  ): Promise<MessengerResult<T>> {
    try {
      return messengerOk(normalize(await command()));
    } catch (error) {
      return this.asFailure(error);
    }
  }

  private async conversationCommand(
    id: ConversationId,
    command: () => Promise<unknown>,
  ): Promise<MessengerResult<Conversation>> {
    try {
      const response = await command();
      const item = firstItem(response);
      if (item && (item['conversationId'] || item['id'])) {
        return messengerOk(normalizeConversation(item));
      }
      return await this.readConversation(id);
    } catch (error) {
      return this.asFailure(error);
    }
  }

  private async readConversation(
    id: ConversationId,
  ): Promise<MessengerResult<Conversation>> {
    const response = await this.request(
      `/current-user/conversations/${encodeURIComponent(id)}`,
      { query: { includeMessages: false, includeProviderReadback: false } },
    );
    const item = firstItem(response);
    if (!item) {
      return messengerFailure('not-found', 'The conversation was not found.', {
        status: 404,
      });
    }
    return messengerOk(normalizeConversation(item));
  }

  private async safeRead<T>(
    read: (signal: AbortSignal) => Promise<T>,
    signal?: AbortSignal,
  ): Promise<MessengerResult<T>> {
    let lastFailure: MessengerFailure | null = null;
    for (let attempt = 0; attempt < this.readAttempts; attempt++) {
      try {
        return messengerOk(await read(signal ?? new AbortController().signal));
      } catch (error) {
        if (
          error instanceof MessengerAbortError ||
          signal?.aborted ||
          !(error instanceof HttpFailure)
        ) {
          return this.asFailure(error);
        }
        lastFailure = error.failure;
        if (!isTransient(error.failure) || attempt === this.readAttempts - 1) {
          return error.failure;
        }
      }
    }
    return (
      lastFailure ??
      messengerFailure('server', 'The request could not be completed.')
    );
  }

  private async request(
    path: string,
    options: RequestOptions = {},
  ): Promise<unknown> {
    const config = this.options.config();
    if (config.mode !== 'remote') {
      throw new HttpFailure(
        messengerFailure(
          'server',
          'Messenger service is not configured for remote access.',
        ),
        'MESSENGER_MODE_LOCAL',
      );
    }
    if (options.signal?.aborted) throw new MessengerAbortError();

    const token = await this.options.tokenProvider();
    if (options.signal?.aborted) throw new MessengerAbortError();
    if (!token) {
      throw new HttpFailure(
        messengerFailure('denied', 'Sign-in is required to continue.', {
          status: 401,
        }),
        'AUTH_REDIRECT',
      );
    }

    const baseUrl = resolveMessengerApiBaseUrl(config.apiBaseUrl);
    const url = new URL(`${baseUrl}${path}`, safeOrigin());
    const query: Record<string, string | number | boolean | undefined> = {
      businessUnit: config.businessUnit || undefined,
      ...options.query,
    };
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }

    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      config.requestTimeoutMs,
    );
    const linked = linkSignals(controller.signal, options.signal);
    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), {
        method: options.method ?? 'GET',
        headers: {
          accept: 'application/json',
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
          'x-correlation-id': this.correlationIdFactory(),
        },
        body:
          options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: linked,
      });
    } catch {
      if (linked.aborted || options.signal?.aborted) {
        if (options.signal?.aborted) throw new MessengerAbortError();
        throw new HttpFailure(
          messengerFailure('server', 'The request timed out. Try again.', {
            retryable: true,
          }),
          'REQUEST_TIMEOUT',
        );
      }
      throw new HttpFailure(
        messengerFailure('offline', 'You appear to be offline. Try again.', {
          retryable: true,
        }),
        'NETWORK',
      );
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok) {
      const body = await readErrorBody(response);
      throw new HttpFailure(statusFailure(response.status, body), body.code);
    }
    if (response.status === 204) return null;
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      throw new HttpFailure(
        messengerFailure('server', 'The service returned an invalid response.'),
        'INVALID_JSON',
      );
    }
  }

  private asFailure<T>(error: unknown): MessengerResult<T> {
    if (error instanceof MessengerAbortError) throw error;
    if (error instanceof MessengerTransportUnavailableError) {
      return messengerFailure(
        'unavailable',
        'Real-time message sending is unavailable in this environment.',
        { retryable: true },
      );
    }
    if (error instanceof HttpFailure) return error.failure;
    if (
      (error instanceof DOMException && error.name === 'AbortError') ||
      (error instanceof Error && error.name === 'AbortError')
    ) {
      throw new MessengerAbortError();
    }
    return messengerFailure(
      'server',
      'The request could not be completed. Try again.',
      { retryable: true },
    );
  }
}

/**
 * The suggestions endpoint answers 200 with an empty batch and an
 * `availability` note when it cannot help. Surface the reason in plain words
 * instead of treating "feature off for this user" like "no ideas this time".
 */
function recommendationsFrom(response: unknown): Recommendation[] {
  const items = normalizeRecommendations(response);
  const record = asRecord(asRecord(response)?.['data']) ?? asRecord(response);
  if (items.length || record?.['availability'] !== 'unavailable') return items;
  const reason = textValue(record['statusReason']);
  if (reason === 'suggested-replies-inactive') {
    throw new HttpFailure(
      messengerFailure('denied', 'Suggested replies are not turned on for your account.', {
        retryable: false,
      }),
      reason,
    );
  }
  if (reason && reason !== 'missing-trigger-message') {
    throw new HttpFailure(
      messengerFailure('server', 'Reply ideas are not available for this conversation yet.', {
        retryable: true,
      }),
      reason,
    );
  }
  return items;
}

const MCS_GUID_ID = /^mcs-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function preferredConversationId(candidate: string, current: string): boolean {
  return MCS_GUID_ID.test(candidate) && !MCS_GUID_ID.test(current);
}

function isTransient(failure: MessengerFailure): boolean {
  return (
    failure.kind === 'offline' ||
    (failure.kind === 'server' && (failure.retryable || (failure.status ?? 0) >= 500))
  );
}

interface ErrorBody {
  readonly code: string;
  readonly message: string;
  readonly retryable?: boolean;
}

function statusFailure(status: number, body?: ErrorBody): MessengerFailure {
  switch (true) {
    case status === 400 || status === 422:
      return messengerFailure(
        'validation',
        'The request was rejected by the service.',
        { status },
      );
    case status === 401:
      return messengerFailure('denied', 'Your session has expired. Sign in again.', {
        status,
      });
    case status === 403:
      return messengerFailure(
        'denied',
        'You do not have access to this conversation.',
        { status },
      );
    case status === 404:
      return messengerFailure(
        'not-found',
        'The conversation was not found.',
        { status },
      );
    case (status === 409 || status === 412 || status === 428) &&
      body?.retryable === false &&
      !!body.message:
      return messengerFailure('conflict', body.message.slice(0, 200), { status });
    case status === 409 || status === 412 || status === 428:
      return messengerFailure(
        'conflict',
        'The conversation changed since you loaded it. Refresh and try again.',
        { status, retryable: true },
      );
    case status >= 500:
      return messengerFailure(
        'server',
        'The messaging service is unavailable. Try again.',
        { status, retryable: true },
      );
    default:
      return messengerFailure('server', 'The request could not be completed.', {
        status,
      });
  }
}

async function readErrorBody(response: Response): Promise<ErrorBody> {
  try {
    const payload = (await response.json()) as Record<string, unknown> | null;
    const nested = asRecord(payload?.['error']);
    const code = payload?.['code'] ?? nested?.['code'];
    const message = payload?.['message'] ?? nested?.['message'];
    const retryable = payload?.['retryable'] ?? nested?.['retryable'];
    return {
      code: typeof code === 'string' ? code : '',
      message: typeof message === 'string' ? message : '',
      retryable: typeof retryable === 'boolean' ? retryable : undefined,
    };
  } catch {
    return { code: '', message: '' };
  }
}

function linkSignals(
  internal: AbortSignal,
  external?: AbortSignal,
): AbortSignal {
  if (!external) return internal;
  if (external.aborted) return external;
  const controller = new AbortController();
  const abort = () => controller.abort();
  internal.addEventListener('abort', abort, { once: true });
  external.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

function entityRecord(response: unknown): JsonRecord {
  const record = asRecord(response);
  return (
    asRecord(record?.['note']) ??
    asRecord(record?.['followUp']) ??
    asRecord(record?.['item']) ??
    firstItem(response) ??
    {}
  );
}

function resolveMessengerApiBaseUrl(apiBaseUrl: string): string {
  const trimmed = apiBaseUrl.replace(/\/+$/, '');
  if (typeof location === 'undefined') return trimmed;
  try {
    const hostUrl = new URL(location.origin);
    const isLocalDev = ['localhost', '127.0.0.1'].includes(hostUrl.hostname);
    if (!isLocalDev) return trimmed;
    const parsed = new URL(trimmed, location.origin);
    if (
      /^ldapi-[a-z0-9-]+\.loandepotdev\.works$/i.test(parsed.hostname) &&
      parsed.pathname.includes('/omni-messenger')
    ) {
      return '/api/messenger';
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

function safeOrigin(): string {
  return typeof location !== 'undefined' && location.origin
    ? location.origin
    : 'http://localhost';
}

function sentMessageFallback(
  sendIntentId: string,
  command: SendMessageCommand,
): Message {
  const now = new Date().toISOString();
  return {
    id: sendIntentId || command.clientMessageId,
    clientMessageId: command.clientMessageId,
    direction: 'outbound',
    senderPersona: 'loan-officer',
    senderLabel: 'Loan officer',
    who: 'You',
    initials: 'You',
    channel: (command.channelId ?? 'sms').toUpperCase(),
    sentAt: now,
    timeLabel: new Date(now).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
    status: 'Sent',
    text: command.content,
    attachments: [],
    acknowledged: false,
    failed: false,
    isLastRead: false,
  };
}
